import { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { Brain, Loader, CheckCircle, XCircle, AlertTriangle, Zap } from 'lucide-react'

interface ConversationState {
  status: 'analyzing' | 'needs_clarification' | 'needs_more_clarification' | 'ready_for_validation' | 'checking_duplicates' | 'invalid' | 'pending_more_info' | 'validation_incomplete' | 'error' | 'follow_up_needed'
  conversationId?: string
  round?: number
  progress?: string
  questions?: Array<{
    question: string
    options?: string[]
    type?: 'select' | 'text_input'
    placeholder?: string
    required?: boolean
  }>
  interpretation?: string
  cleanQuestion?: string // AI-cleaned version of the question
  detectedEntities?: any
  dataSources?: string[]
  confidence?: number
  reasoning?: string
  suggestions?: string[]
  issues?: string[] // List of specific validation issues
  hasDuplicates?: boolean
  similarMarkets?: Array<{
    id: number
    question: string
    similarity: number
    reasoning: string
  }>
  recommendation?: 'APPROVE' | 'REJECT' | 'MODIFY'
  // Handle ALL AI response data
  aiMessage?: string
  missingInfo?: string[]
  nextSteps?: string[]
  rawAiResponse?: any
  conversationHistory?: Array<{
    type: 'user' | 'ai'
    message: string
    timestamp: Date
  }>
  pendingReason?: string
  canRetry?: boolean
}

interface ConversationalValidationProps {
  question: string
  category: string
  onApproved: (analysisData: any) => void
  onCancel: () => void
}

export default function ConversationalValidation({ 
  question, 
  category, 
  onApproved, 
  onCancel 
}: ConversationalValidationProps) {
  const [conversation, setConversation] = useState<ConversationState | null>(null)
  const [isValidating, setIsValidating] = useState(false)
  const [clarificationAnswers, setClarificationAnswers] = useState<Record<string, string>>({})


  // NEVER start automatically - only when user triggers it
  const [hasStarted, setHasStarted] = useState(false);
  
  // NO AUTOMATIC EFFECTS - user must click button

  const startConversation = async () => {
    try {
      setIsValidating(true)
      setConversation({ status: 'analyzing' })

      console.log('🤖 Starting AI conversation for:', question)

      const analysisResponse = await axios.post('/api/analyze-question', {
        question,
        category
      })

      setConversation(analysisResponse.data)

      if (analysisResponse.data.status === 'ready_for_validation') {
        // Check for duplicates
        setConversation(prev => ({ ...prev!, status: 'checking_duplicates' }))
        
        const duplicateResponse = await axios.get('/api/check-duplicates', {
          params: { question }
        })

        if (duplicateResponse.data.hasDuplicates) {
          setConversation(prev => ({
            ...prev!,
            hasDuplicates: true,
            similarMarkets: duplicateResponse.data.similarMarkets,
            recommendation: duplicateResponse.data.recommendation
          }))
        } else {
          // Approve immediately
          console.log('✅ AI APPROVED IMMEDIATELY WITH DATA:', analysisResponse.data);
          console.log('📅 Backend dates from initial analysis:', {
            bettingEndsAt: analysisResponse.data.bettingEndsAt,
            resolutionTime: analysisResponse.data.resolutionTime
          });
          onApproved(analysisResponse.data)
        }
      }

    } catch (error: any) {
      console.error('AI conversation failed:', error)
      // Don't show error immediately - retry once
      setTimeout(() => {
        if (!conversation || conversation.status === 'invalid') {
          setConversation({ status: 'invalid', reasoning: 'AI service temporarily unavailable' })
        }
      }, 3000)
    } finally {
      setIsValidating(false)
    }
  }

  const submitClarification = async () => {
    if (!conversation?.conversationId) return

    try {
      setIsValidating(true)

      const clarificationResponse = await axios.post('/api/clarify-question', {
        conversationId: conversation.conversationId,
        clarification: Object.values(clarificationAnswers).join('; '),
        selectedOption: clarificationAnswers.selectedOption
      })

      setConversation(clarificationResponse.data)

      if (clarificationResponse.data.status === 'ready_for_validation') {
        console.log('✅ BACKEND APPROVED WITH DATA:', clarificationResponse.data);
        console.log('📅 Backend dates:', {
          bettingEndsAt: clarificationResponse.data.bettingEndsAt,
          resolutionTime: clarificationResponse.data.resolutionTime
        });
        onApproved(clarificationResponse.data)
      }

    } catch (error: any) {
      console.error('Clarification failed:', error)
      toast.error('Clarification failed')
    } finally {
      setIsValidating(false)
    }
  }

  // Show button to start conversation if not started yet
  if (!conversation && !hasStarted) {
    return (
      <div className="card p-10 md:p-12 lg:p-16 text-center bg-gradient-to-br from-[#052393]/20 to-[#2AAAEA]/10 border-[#2AAAEA]/40 min-h-[400px] lg:min-h-[500px] flex flex-col items-center justify-center">
        <img 
          src="/cpx-logo.jpg" 
          alt="CPX AI" 
          className="w-20 h-20 lg:w-24 lg:h-24 rounded-2xl mx-auto mb-6 shadow-lg shadow-[#2AAAEA]/30"
        />
        <h3 className="text-3xl lg:text-4xl font-black text-white mb-3">Ready for AI Analysis</h3>
        <p className="text-gray-300 mb-8 text-lg max-w-md">Chat with our AI to design and validate your prediction pool</p>
        <button
          onClick={() => {
            setHasStarted(true);
            startConversation();
          }}
          className="bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white font-bold px-10 py-4 rounded-xl flex items-center space-x-3 mx-auto transition-all transform hover:scale-105 text-lg"
        >
          <Zap className="w-6 h-6" />
          <span>Start AI Conversation</span>
        </button>
      </div>
    )
  }

  // This is the loading state AFTER conversation has started
  if (!conversation) {
    return (
      <div className="card p-10 md:p-12 text-center bg-gradient-to-br from-[#052393]/20 to-[#2AAAEA]/10 border-[#2AAAEA]/40 min-h-[400px] lg:min-h-[500px] flex flex-col items-center justify-center">
        <div className="flex flex-col items-center space-y-6">
          <img 
            src="/cpx-logo.jpg" 
            alt="CPX AI" 
            className="w-20 h-20 rounded-2xl shadow-lg shadow-[#2AAAEA]/30 animate-pulse"
          />
          <div className="space-y-2">
            <p className="text-white font-bold text-2xl">🤖 AI Expert Working...</p>
            <p className="text-gray-300 text-base">Building your pool validation plan</p>
          </div>
          <div className="text-sm text-gray-400 space-y-3 mt-4">
            <p className="flex items-center justify-center space-x-2"><span className="text-cpx-light-blue">•</span><span>Searching for accurate data sources</span></p>
            <p className="flex items-center justify-center space-x-2"><span className="text-cpx-light-blue">•</span><span>Analyzing validation requirements</span></p>
            <p className="flex items-center justify-center space-x-2"><span className="text-cpx-light-blue">•</span><span>Building detailed resolution plan</span></p>
            <p className="flex items-center justify-center space-x-2"><span className="text-cpx-light-blue">•</span><span>Ensuring blockchain compatibility</span></p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card p-8 md:p-10 lg:p-12 bg-gradient-to-br from-[#052393]/20 to-[#2AAAEA]/10 border-[#2AAAEA]/40 min-h-[500px] lg:min-h-[600px]">
      <div className="flex items-center space-x-4 mb-6 pb-4 border-b border-[#2AAAEA]/20">
        <img 
          src="/cpx-logo.jpg" 
          alt="CPX AI" 
          className="w-12 h-12 rounded-xl shadow-lg shadow-[#2AAAEA]/30"
        />
        <div>
          <h3 className="font-bold text-white text-xl">
            🤖 AI Analyst {conversation.round && conversation.round > 1 ? `(Round ${conversation.round})` : ''}
          </h3>
          <p className="text-base text-cpx-light-blue font-medium">
            {conversation.status === 'analyzing' && 'Analyzing your question...'}
            {conversation.status === 'needs_clarification' && 'I need some details'}
            {conversation.status === 'needs_more_clarification' && 'Just a couple more questions...'}
            {conversation.status === 'checking_duplicates' && 'Checking for duplicates...'}
            {conversation.status === 'ready_for_validation' && 'Analysis complete!'}
            {conversation.status === 'invalid' && 'Question needs improvement'}
          </p>
          {conversation.progress && (
            <p className="text-sm text-gray-300 mt-1">
              💭 {conversation.progress}
            </p>
          )}
        </div>
      </div>

      {/* Clarification Questions - Handles both initial and follow-up rounds */}
      {(conversation.status === 'needs_clarification' || conversation.status === 'needs_more_clarification') && conversation.questions && (
        <div className="space-y-6">
          {conversation.status === 'needs_more_clarification' ? (
            <div className="bg-[#FBB03B]/10 border border-[#FBB03B]/30 p-4 rounded-xl mb-4">
              <p className="text-cpx-yellow font-bold text-lg">
                🔄 Follow-up Questions (Round {conversation.round || 2})
              </p>
              <p className="text-gray-300 mt-1">
                {conversation.progress || "Based on your previous answers, I need a bit more information..."}
              </p>
            </div>
          ) : (
            <p className="text-white font-bold text-lg mb-2">💬 I need some details to set up your market:</p>
          )}
          
          <div className="grid gap-5 md:grid-cols-2">
            {conversation.questions.map((q, index) => (
              <div key={index} className="bg-[#001233] p-5 rounded-xl border border-[#052393]/40 hover:border-[#2AAAEA]/40 transition-colors">
                <label className="block text-base font-bold text-white mb-3">
                  {q.question}
                  {q.required && <span className="text-red-400"> *</span>}
                </label>
                {q.options ? (
                  <select 
                    className="input text-base py-3"
                    onChange={(e) => setClarificationAnswers(prev => ({
                      ...prev,
                      [q.question]: e.target.value,
                      selectedOption: e.target.value
                    }))}
                  >
                    <option value="">Select an option...</option>
                    {q.options.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="input text-base py-3"
                    placeholder={q.placeholder}
                    onChange={(e) => setClarificationAnswers(prev => ({
                      ...prev,
                      [q.question]: e.target.value
                    }))}
                  />
                )}
              </div>
            ))}
          </div>
          
          <div className="flex justify-between pt-6 border-t border-[#052393]/30">
            <button onClick={onCancel} className="btn-secondary px-6 py-3 text-base">
              Start Over
            </button>
              <button
                onClick={submitClarification}
                disabled={isValidating}
                className="btn-primary px-8 py-3 text-base font-bold"
              >
                {isValidating ? 'Processing...' : conversation.status === 'needs_more_clarification' ? 'Submit Follow-up' : 'Submit Answers'}
              </button>
          </div>
        </div>
      )}

      {/* Duplicate Warning */}
      {conversation.hasDuplicates && (
        <div className="bg-[#FBB03B]/10 border border-[#FBB03B]/30 p-4 rounded-lg">
          <h4 className="font-bold text-cpx-yellow mb-2">⚠️ Similar markets found:</h4>
          <div className="space-y-2 mb-4">
            {conversation.similarMarkets?.map(market => (
              <div key={market.id} className="text-sm bg-[#001233] p-3 rounded-lg border border-[#052393]/40 text-white">
                "{market.question}" ({market.similarity}% similar)
              </div>
            ))}
          </div>
          
          <div className="flex space-x-3">
            <button onClick={onCancel} className="btn-secondary text-sm">
              Modify Question
            </button>
            <button
              onClick={() => onApproved(conversation)}
              className="btn-primary text-sm"
            >
              Proceed Anyway
            </button>
          </div>
        </div>
      )}

      {/* AI Needs More Information */}
      {(conversation.status === 'pending_more_info' || conversation.status === 'validation_incomplete' || conversation.status === 'follow_up_needed') && (
        <div className="bg-[#FBB03B]/10 border border-[#FBB03B]/30 p-4 rounded-lg">
          <h4 className="font-bold text-cpx-yellow mb-2">⏳ AI Needs More Information</h4>
          <div className="space-y-2 text-sm text-gray-300 mb-4">
            <p><strong className="text-white">Status:</strong> {conversation.status.replace('_', ' ').toUpperCase()}</p>
            {conversation.pendingReason && <p><strong className="text-white">Reason:</strong> {conversation.pendingReason}</p>}
            {conversation.aiMessage && <p><strong className="text-white">AI Message:</strong> {conversation.aiMessage}</p>}
            {conversation.missingInfo && conversation.missingInfo.length > 0 && (
              <div>
                <strong className="text-white">Missing Information:</strong>
                <ul className="list-disc ml-4">
                  {conversation.missingInfo.map((info, i) => (
                    <li key={i}>{info}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-[#052393]/40 border border-[#052393]/60 text-white rounded-lg hover:bg-[#052393]/60 font-bold transition-all"
            >
              🔄 Start Over
            </button>
            <button
              onClick={() => {
                // Go back to clarification questions
                setConversation(prev => ({
                  ...prev,
                  status: 'needs_clarification'
                }));
              }}
              className="px-4 py-2 bg-gradient-to-r from-[#FBB03B] to-[#FBB03B]/80 text-white rounded-lg hover:shadow-lg hover:shadow-[#FBB03B]/30 font-bold transition-all"
            >
              📝 Fix My Answers
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {conversation.status === 'error' && (
        <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-lg">
          <h4 className="font-bold text-red-400 mb-2">❌ AI Analysis Failed</h4>
          <div className="space-y-2 text-sm text-gray-300 mb-4">
            <p>Something went wrong with the AI analysis.</p>
            {conversation.aiMessage && <p><strong className="text-white">Error:</strong> {conversation.aiMessage}</p>}
            {conversation.rawAiResponse && (
              <details className="mt-2">
                <summary className="cursor-pointer font-medium text-white">Show Technical Details</summary>
                <pre className="mt-2 text-xs bg-[#001233] p-2 rounded overflow-auto border border-[#052393]/40 text-gray-300">
                  {JSON.stringify(conversation.rawAiResponse, null, 2)}
                </pre>
              </details>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-red-600/80 text-white rounded-lg hover:bg-red-600 font-bold transition-all"
            >
              🔄 Start Over
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#052393]/60 text-white rounded-lg hover:bg-[#052393]/80 font-bold transition-all"
            >
              🔃 Refresh Page
            </button>
          </div>
        </div>
      )}

      {/* Ready for validation */}
      {conversation.status === 'ready_for_validation' && conversation.interpretation && !conversation.hasDuplicates && (
        <div className="bg-[#009245]/10 border border-[#009245]/30 p-6 rounded-lg">
          <h4 className="font-bold text-cpx-green text-lg mb-3">✅ AI Analysis Complete</h4>
          <div className="space-y-2 text-sm text-gray-300 mb-4">
            <p><strong className="text-white">Interpretation:</strong> {conversation.interpretation}</p>
            <p><strong className="text-white">Data Sources:</strong> {conversation.dataSources?.join(', ')}</p>
            <p><strong className="text-white">Confidence:</strong> <span className="text-cpx-green font-bold">{conversation.confidence}%</span></p>
          </div>
          
          <button
            onClick={() => {
              console.log('✅ USER CLICKED APPROVE WITH CONVERSATION:', conversation);
              // Pass all the conversation data including dates
              onApproved(conversation);
            }}
            className="bg-gradient-to-r from-[#009245] to-emerald-600 hover:shadow-lg hover:shadow-[#009245]/30 text-white font-bold px-6 py-3 rounded-xl w-full transition-all transform hover:scale-105"
          >
            ✅ Approve & Continue
          </button>
        </div>
      )}

      {/* Invalid */}
      {conversation.status === 'invalid' && (
        <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-lg">
          <h4 className="font-bold text-red-400 mb-2">❌ Cannot Create This Pool</h4>
          <p className="text-sm text-gray-300 mb-4">{conversation.reasoning}</p>
          
          {/* Show suggestions for better questions */}
          {conversation.suggestions && conversation.suggestions.length > 0 && (
            <div className="bg-[#FBB03B]/10 border border-[#FBB03B]/30 p-3 rounded-lg">
              <p className="text-xs font-bold text-cpx-yellow mb-2">💡 Try This Instead:</p>
              <ul className="space-y-1">
                {conversation.suggestions.map((suggestion: string, i: number) => (
                  <li key={i} className="text-sm text-gray-300 flex items-start">
                    <span className="mr-2">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="flex space-x-2">
            <button 
              onClick={onCancel} 
              className="btn-secondary flex-1"
            >
              🔄 Start Over with New Question
            </button>
            <button
              onClick={() => {
                // Clear conversation and retry with same question
                setConversation(null)
                setTimeout(() => startConversation(), 100)
              }}
              className="btn-secondary flex-1"
            >
              🔁 Retry This Question
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
